import { useHandler } from "@redotech/react-util/hook";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";

export interface RejectedShipmentRow {
  groupId: string;
  orderName: string;
  reason: string;
  productSku?: string;
  productVariantTitle?: string;
  productImageUrl?: string;
}

interface RejectedShipmentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Set when the pick session was partially created — surfaces a primary
   * action to navigate into the new session in addition to "Close". */
  onViewPickSession?: () => void;
  rows: RejectedShipmentRow[];
}

export const RejectedShipmentsModal = memo(function RejectedShipmentsModal({
  isOpen,
  onClose,
  onViewPickSession,
  rows,
}: RejectedShipmentsModalProps) {
  const handleViewPickSession = useHandler(() => {
    onViewPickSession?.();
    onClose();
  });

  const title = onViewPickSession
    ? "Some fulfillments couldn't be added"
    : "No fulfillments could be added";
  const subtitle = onViewPickSession
    ? `${rows.length} fulfillment${rows.length === 1 ? "" : "s"} couldn't be added to the new pick session. The rest were added successfully.`
    : `${rows.length} fulfillment${rows.length === 1 ? "" : "s"} were checked but none could be added to a pick session.`;

  return (
    <RedoModal
      isOpen={isOpen}
      modalSize="sm"
      onModalCloseRequested={onClose}
      subtitle={subtitle}
      title={title}
    >
      <Flex dir="column" gap="xl" w="full">
        <Flex
          dir="column"
          gap="none"
          style={{ maxHeight: "60vh", overflowY: "auto" }}
          w="full"
        >
          {rows.map((row, index) => (
            <Flex
              dir="column"
              gap="xs"
              key={`${row.groupId}-${index}`}
              style={{
                borderBottom:
                  index < rows.length - 1
                    ? "1px solid var(--redo-colors-border-border-secondary)"
                    : undefined,
                paddingBottom:
                  index < rows.length - 1
                    ? "var(--redo-spacing-md)"
                    : undefined,
                paddingTop: index > 0 ? "var(--redo-spacing-md)" : undefined,
              }}
            >
              <Text fontSize="sm" fontWeight="semibold">
                {row.orderName}
              </Text>
              {(row.productImageUrl ||
                row.productVariantTitle ||
                row.productSku) && (
                <Flex align="center" gap="sm">
                  {row.productImageUrl && (
                    <img
                      alt={row.productVariantTitle ?? row.productSku ?? ""}
                      src={row.productImageUrl}
                      style={{
                        width: 36,
                        height: 36,
                        objectFit: "cover",
                        borderRadius: "var(--border-radius-sm)",
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <Flex dir="column" gap="none">
                    {row.productVariantTitle && (
                      <Text fontSize="xs">{row.productVariantTitle}</Text>
                    )}
                    {row.productSku && (
                      <Text fontSize="xs" textColor="secondary">
                        {row.productSku}
                      </Text>
                    )}
                  </Flex>
                </Flex>
              )}
              <Text fontSize="xs" textColor="secondary">
                {row.reason}
              </Text>
            </Flex>
          ))}
        </Flex>
        <Flex gap="lg" justify="flex-end" w="full">
          <RedoButton
            hierarchy="secondary"
            onClick={onClose}
            size="md"
            text="Close"
          />
          {onViewPickSession && (
            <RedoButton
              hierarchy="primary"
              onClick={handleViewPickSession}
              size="md"
              text="View pick session"
            />
          )}
        </Flex>
      </Flex>
    </RedoModal>
  );
});
